import React, { useEffect, useState } from 'react';
import { useHomepageSettings } from '../../hooks/useHomepageSettings';

const HeroSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { data: settings, isLoading } = useHomepageSettings();

  // Extract hero settings with fallbacks
  const headline =
    settings?.hero?.headline || 'Your Second Home, Our First Priority';
  const subheadline =
    settings?.hero?.subheadline ||
    "Premium home watch and concierge services for your vacation property in Destin & 30A. Peace of mind when you're away.";
  const backgroundImage =
    settings?.hero?.backgroundImage?.asset?.url ||
    'https://images.unsplash.com/photo-1559827260-dc66d52bef19?q=80&w=2070';
  const ctaPrimary = {
    text: settings?.hero?.ctaPrimaryText || 'Explore Services',
    link: settings?.hero?.ctaPrimaryLink || '#services',
  };
  const ctaSecondary = {
    text: settings?.hero?.ctaSecondaryText || 'Get a Quote',
    link: settings?.hero?.ctaSecondaryLink || '#contact',
  };

  useEffect(() => {
    // Trigger fade-in animation on mount
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Show loading state while fetching
  if (isLoading) {
    return (
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden bg-primary-700">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </section>
    );
  }

  return (
    <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Background Image with Parallax Effect */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transform scale-105 transition-transform duration-1000"
        style={{
          backgroundImage: `url(${backgroundImage})`,
        }}
      >
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-700/70 via-primary-600/60 to-blue-900/70 animate-gradient" />
      </div>

      {/* Content */}
      <div
        className={`relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto">
          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
            {headline}
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            {subheadline}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {/* Primary CTA */}
            <a
              href={ctaPrimary.link}
              className="group relative px-8 py-4 bg-white text-primary-500 font-semibold rounded-full hover:bg-primary-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 transform inline-flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              {ctaPrimary.text}
              <svg
                className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </a>

            {/* Secondary CTA */}
            <a
              href={ctaSecondary.link}
              className="px-8 py-4 bg-transparent text-white font-semibold rounded-full border-2 border-white/80 hover:bg-white/10 hover:border-white transition-all duration-300 backdrop-blur-sm w-full sm:w-auto text-center"
            >
              {ctaSecondary.text}
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/50 flex items-start justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </div>

      {/* Wave Divider at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-[0]">
        <svg
          className="relative block w-full h-16 sm:h-20 md:h-24"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
            className="fill-sand-50"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
